import React, { useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAppDispatch } from '../store';
import { handleOAuthSuccess } from '../store/slices/authSlice';
import { CircularProgress, Box, Typography } from '@mui/material';

/**
 * Component to handle OAuth success redirects
 * 
 * Extracts the access token from the URL and dispatches the handleOAuthSuccess action
 */
const AuthSuccess: React.FC = () => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  
  useEffect(() => {
    // Get token from URL query parameters
    const params = new URLSearchParams(location.search);
    const token = params.get('access_token');
    
    const processAuthentication = async () => {
      if (token) {
        try {
          // Dispatch action to handle the token and fetch user data
          await dispatch(handleOAuthSuccess(token));
          
          // On success, redirect to dashboard
          navigate('/', { replace: true });
        } catch (error) {
          console.error('Authentication error:', error);
          // On error, redirect to login page
          navigate('/login', { replace: true });
        }
      } else {
        // No token found, redirect to login
        navigate('/login', { replace: true });
      }
    };
    
    processAuthentication();
  }, [dispatch, location.search, navigate]);
  
  return (
    <Box 
      sx={{ 
        display: 'flex', 
        flexDirection: 'column',
        alignItems: 'center', 
        justifyContent: 'center',
        minHeight: '100vh',
        bgcolor: 'background.default'
      }}
    >
      <CircularProgress size={60} sx={{ mb: 4 }} />
      <Typography variant="h5" component="h1" gutterBottom>
        Completing authentication...
      </Typography>
      <Typography variant="body1" color="text.secondary">
        Please wait while we sign you in
      </Typography>
    </Box>
  );
};

export default AuthSuccess; 
